import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Snackbar, Alert, Chip, CircularProgress, Tooltip, Grid, Card, CardContent } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Refresh, Warning, CheckCircle, Key, Settings, Search, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getAPIKeys, createAPIKey, updateAPIKey, deleteAPIKey, activateAPIKey, getAPIProviders } from '../../services/apiManagement';
import { useDebounce } from '../../hooks/useDebounce';

const defaultForm = {
  provider: '',
  name: '',
  environment: 'production',
  api_key: '',
  secret_key: '',
  additional_config: {},
  is_active: true,
  is_primary: false
};

const APIKeyManagement = () => {
  const [keys, setKeys] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Pagination and filtering states
  const [page, setPage] = useState(0); // 0-based for frontend, 1-based for backend
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [environmentFilter, setEnvironmentFilter] = useState('all');
  
  // Calculate total pages and total count
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchKeys = useCallback(async (pageArg, rowsArg, search = '', status = 'all', environment = 'all') => {
    setLoading(true);
    try {
      const response = await getAPIKeys({
        page: pageArg + 1, // backend expects 1-based
        page_size: rowsArg,
        search,
        status: status === 'all' ? '' : status,
        environment: environment === 'all' ? '' : environment
      });
      
      // Ensure keys is always an array
      if (response.data && Array.isArray(response.data.results)) {
        setKeys(response.data.results);
        setTotalPages(response.data.total_pages || 1);
        setTotalCount(response.data.count || 0);
      } else if (Array.isArray(response.data)) {
        setKeys(response.data);
        setTotalPages(1);
        setTotalCount(response.data.length);
      } else {
        setKeys([]);
        setTotalPages(1);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
      setKeys([]); // Set empty array on error
      setTotalPages(1);
      setTotalCount(0);
      setSnackbar({
        open: true,
        message: 'Failed to fetch API keys',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProviders = useCallback(async () => {
    try {
      const response = await getAPIProviders();
      setProviders(response.data || []);
    } catch (error) {
      console.error('Error fetching API providers:', error);
    }
  }, []);

  // Auto-apply filters when debounced values change
  useEffect(() => {
    setPage(0); // Reset to first page when filters change
    fetchKeys(0, rowsPerPage, debouncedSearchTerm, statusFilter, environmentFilter);
    fetchProviders();
  }, [debouncedSearchTerm, statusFilter, environmentFilter, rowsPerPage, fetchKeys, fetchProviders]);

  // Handle pagination changes (only when page > 0 to avoid double fetch)
  useEffect(() => {
    if (page > 0) {
      fetchKeys(page, rowsPerPage, debouncedSearchTerm, statusFilter, environmentFilter);
    }
  }, [page, fetchKeys, rowsPerPage, debouncedSearchTerm, statusFilter, environmentFilter]);

  // Refresh function with current filters
  const handleRefresh = () => {
    setPage(0);
    fetchKeys(0, rowsPerPage, debouncedSearchTerm, statusFilter, environmentFilter);
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };
  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };
  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  // Search and filter handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleEnvironmentFilterChange = (event) => {
    setEnvironmentFilter(event.target.value);
  };

  const handleOpen = (key = null) => {
    if (key) {
      setForm({
        provider: key.provider,
        name: key.name,
        environment: key.environment,
        api_key: key.api_key,
        secret_key: key.secret_key || '',
        additional_config: key.additional_config || {},
        is_active: key.is_active,
        is_primary: key.is_primary
      });
      setEditingId(key.id);
    } else {
      setForm(defaultForm);
      setEditingId(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm(defaultForm);
    setEditingId(null);
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await updateAPIKey(editingId, form);
        setSnackbar({
          open: true,
          message: 'API Key updated successfully',
          severity: 'success'
        });
      } else {
        await createAPIKey(form);
        setSnackbar({
          open: true,
          message: 'API Key created successfully',
          severity: 'success'
        });
      }
      handleClose();
      fetchKeys(page, rowsPerPage, debouncedSearchTerm, statusFilter, environmentFilter);
    } catch (error) {
      console.error('Error saving API key:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save API key',
        severity: 'error'
      });
    }
  };

  const handleDeleteClick = (key) => {
    setDeleteId(key.id);
    setDeleteName(key.name);
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
    setDeleteName('');
  };

  const handleDelete = async () => {
    try {
      await deleteAPIKey(deleteId);
      setSnackbar({
        open: true,
        message: 'API Key deleted successfully',
        severity: 'success'
      });
      setDeleteId(null);
      setDeleteName('');
      fetchKeys(page, rowsPerPage, debouncedSearchTerm, statusFilter, environmentFilter);
    } catch (error) {
      console.error('Error deleting API key:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete API key',
        severity: 'error'
      });
    }
  };

  const handleActivate = async (keyId) => {
    try {
      await activateAPIKey(keyId);
      setSnackbar({
        open: true,
        message: 'API Key activated successfully',
        severity: 'success'
      });
      fetchKeys(page, rowsPerPage, debouncedSearchTerm, statusFilter, environmentFilter);
    } catch (error) {
      console.error('Error activating API key:', error);
      setSnackbar({
        open: true,
        message: 'Failed to activate API key',
        severity: 'error'
      });
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  const getEnvironmentColor = (environment) => {
    switch (environment) {
      case 'production': return 'error';
      case 'staging': return 'warning';
      case 'development': return 'info';
      default: return 'default';
    }
  };

  // Add serial numbers to keys for the current page
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    { field: 'sn', headerName: 'S/N', width: 80, sortable: false, filterable: false },
    {
      field: 'provider_name',
      headerName: 'Provider',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'name',
      headerName: 'Key Name',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'environment',
      headerName: 'Environment',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={getEnvironmentColor(params.value)}
        />
      )
    },
    {
      field: 'api_key',
      headerName: 'API Key',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-all' }}>
          {params.value ? `${params.value.substring(0, 8)}...` : 'N/A'}
        </Typography>
      )
    },
    {
      field: 'is_primary',
      headerName: 'Primary',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          size="small"
          color={params.value ? 'primary' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
        />
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          size="small"
          color={getStatusColor(params.value)}
        />
      )
    },
    {
      field: 'usage_count',
      headerName: 'Usage',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary">
          {params.value || 0}
        </Typography>
      )
    },
    {
      field: 'last_used',
      headerName: 'Last Used',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary">
          {params.value ? new Date(params.value).toLocaleDateString() : 'Never'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleOpen(params.row)}
              color="primary"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          {!params.row.is_active && (
            <Tooltip title="Activate">
              <IconButton
                size="small"
                onClick={() => handleActivate(params.row.id)}
                color="success"
              >
                <CheckCircle fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row)}
              color="error"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            API Key Management
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage API keys for different providers and environments
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
          >
            Add API Key
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                placeholder="Search API keys..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                size="small"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Environment"
                value={environmentFilter}
                onChange={handleEnvironmentFilterChange}
                size="small"
              >
                <MenuItem value="all">All Environments</MenuItem>
                <MenuItem value="development">Development</MenuItem>
                <MenuItem value="staging">Staging</MenuItem>
                <MenuItem value="production">Production</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {totalCount}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Keys
                  </Typography>
                </Box>
                <Key color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {Array.isArray(keys) ? keys.filter(k => k.is_active).length : 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Active Keys
                  </Typography>
                </Box>
                <CheckCircle color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {Array.isArray(keys) ? keys.filter(k => k.is_primary).length : 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Primary Keys
                  </Typography>
                </Box>
                <Settings color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {Array.isArray(keys) ? new Set(keys.map(k => k.provider_name)).size : 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Providers
                  </Typography>
                </Box>
                <Settings color="info" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* API Keys Table */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            {keys.length === 0 && !loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  No API keys found
                </Typography>
                <Button variant="outlined" onClick={handleRefresh}>
                  Refresh Data
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(Array.isArray(keys) ? keys : [], page, rowsPerPage)}
                columns={columns}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1400, // Ensure minimum width for all columns
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: theme => theme.palette.primary.main,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            Rows per page:
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder="Rows"
            />
          )}
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit API Key' : 'Add API Key'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  label="Provider"
                  value={form.provider}
                  onChange={handleChange('provider')}
                  fullWidth
                  required
                >
                  {providers.map((provider) => (
                    <MenuItem key={provider.id} value={provider.id}>
                      {provider.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Key Name"
                  value={form.name}
                  onChange={handleChange('name')}
                  fullWidth
                  required
                  placeholder="e.g., Main Key, Backup Key"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  label="Environment"
                  value={form.environment}
                  onChange={handleChange('environment')}
                  fullWidth
                  required
                >
                  <MenuItem value="development">Development</MenuItem>
                  <MenuItem value="staging">Staging</MenuItem>
                  <MenuItem value="production">Production</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="API Key"
                  value={form.api_key}
                  onChange={handleChange('api_key')}
                  fullWidth
                  required
                  placeholder="Enter the API key"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Secret Key (Optional)"
                  value={form.secret_key}
                  onChange={handleChange('secret_key')}
                  fullWidth
                  placeholder="Enter the secret key if required"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  label="Status"
                  value={form.is_active}
                  onChange={handleChange('is_active')}
                  fullWidth
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  label="Primary Key"
                  value={form.is_primary}
                  onChange={handleChange('is_primary')}
                  fullWidth
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the API key "{deleteName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default APIKeyManagement;